<template>
  <div class="modal" :class="{ 'is-active': isActive }">
    <div class="modal-background"></div>
    <div class="modal-card">
      <header class="modal-card-head">
        <p class="modal-card-title">{{ isEdit ? 'Edit User' : 'Create User' }}</p>
        <button class="delete" aria-label="close" @click="$emit('close')"></button>
      </header>
      <section class="modal-card-body">
        <form @submit.prevent="submitForm">

          <div class="field" style="margin-bottom: 40px;">
            <label class="label">Email</label>
            <div class="control">
              <input class="input" type="email" v-model="localUser.email">
            </div>
          </div>
          <div class="field" style="margin-bottom: 40px;">
            <label class="label">First Name</label>
            <div class="control">
              <input class="input" type="text" v-model="localUser.first_name">
            </div>
          </div>
          <div class="field" style="margin-bottom: 40px;">
            <label class="label">Last Name</label>
            <div class="control">
              <input class="input" type="text" v-model="localUser.last_name">
            </div>
          </div>
          <div class="field" style="margin-bottom: 40px;">
            <label class="label">Roles</label>
            <div class="control no-arrow">
              <div class="select is-fullwidth no-arrow">
                <select v-model="localUser.access_type">
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                  <option value="superadmin">Superadmin</option>
                </select>
              </div>
            </div>
          </div>
          <div class="field" style="margin-bottom: 140px;">
            <label class="label">Role</label>
            <div class="control no-arrow">
              <div class="select is-fullwidth no-arrow">
                <select v-model="localUser.groups" multiple required>
                  <option v-for="group in allGroups" :key="group.id" :value="group.id">{{ group.name }}</option>
                </select>
              </div>
            </div>
          </div>
          <div class="field" style="margin-bottom: 140px;">
            <label class="label">Accounts</label>
            <div class="control no-arrow">
              <div class="select is-fullwidth no-arrow">
                <select v-model="localUser.sender_entities" multiple>
                  <option v-for="entity in allSenderEntities" :key="entity.id" :value="entity.id">{{ entity.name }}</option>
                </select>
              </div>
            </div>
          </div>
          <div class="field" style="margin-bottom: 140px;">
            <label class="label">Store Codes</label>
            <div class="control no-arrow">
              <div class="select is-fullwidth no-arrow">
                <select v-model="localUser.store_code" multiple>
                  <option v-for="store in allStoreCodes" :key="store.id" :value="store.id">{{ store.name }}</option>
                </select>
              </div>
            </div>
          </div>
          <div v-if="!isEdit" class="field" style="margin-bottom: 140px;">
            <label class="label">Temporary Password</label>
            <div class="control">
              <input class="input" type="password" v-model="localUser.password">
            </div>
          </div>
        </form>
      </section>
      <footer class="buttons modal-card-foot">
        <button class="button is-success" @click="submitForm">{{ isEdit ? 'Update' : 'Create' }}</button>
        <button v-if="isEdit && localUser.is_active" type="button" class="button is-warning" @click="toggleUserLock">Lock User</button>
        <button v-if="isEdit && !localUser.is_active" type="button" class="button is-info" @click="toggleUserLock">Unlock User</button>
        <!-- <button v-if="isEdit" type="button" class="button is-warning" @click="resetPassword">Reset Password</button> -->
        <button v-if="isEdit" type="button" class="button is-danger" @click="confirmDeleteUser">Delete User</button>
        <button class="button" @click="$emit('close')">Cancel</button>
      </footer>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { toast } from 'bulma-toast';

export default {
  props: {
    isActive: Boolean,
    isEdit: Boolean,
    user: Object,
    allGroups: Array,
    allSenderEntities: Array,
    allStoreCodes: Array,
  },
  data() {
    return {
      localUser: this.user
        ? { ...this.user }
        : {
            username: '',
            email: '',
            first_name: '',
            last_name: '',
            access_type: '',
            sender_entities: [],
            store_code: [],
            groups: [],
            password: '',
            is_active: '',
          },
    };
  },
  watch: {
    user: {
      immediate: true,
      handler(newUser) {
        console.log('User prop changed:', newUser);
        this.localUser = { ...newUser };
        console.log('Initialized localUser:', this.localUser);
      },
    },
  },
  methods: {
    submitForm() {
      if (!this.isEdit) {
        this.localUser.username = this.localUser.email; // Ensure username is set to email
      }
      // Remove the notifications preferences before emitting the save event
      delete this.localUser.notification_preferences;
      this.$emit('save', this.localUser);
    },
    async toggleUserLock() {
      const newStatus = !this.localUser.is_active;
      try {
        const response = await axios.put(`/api/v1/user-profile/update/${this.localUser.id}/`, {
          is_active: newStatus,
        }, {
          headers: {
            Authorization: `Token ${localStorage.getItem('token')}`,
          },
        });
        this.localUser.is_active = response.data.is_active; // Update local state
        const status = newStatus ? 'unlocked' : 'locked';
        
        // Show success message
        toast({
          message: `User has been successfully ${status}.`,
          type: 'is-success',
          dismissible: true,
          duration: 3000,
          position: 'top-right',
        });
        
        // Emit save event to trigger refresh and close modal
        this.$emit('save', this.localUser);
        
      } catch (error) {
        console.error('Error locking/unlocking user:', error);
        
        // Show error message
        toast({
          message: 'There was an error updating the user status.',
          type: 'is-danger',
          dismissible: true,
          duration: 5000,
          position: 'top-right',
        });
      }
    },
    resetPassword() {
      axios.post('/api/v1/user/reset-password-admin/', {
        email: this.localUser.email,
      })
      .then(response => {
        console.log('Password reset email sent:', response.data);
        toast({
          message: 'Password reset email sent.',
          type: 'is-success',
          dismissible: true,
          duration: 3000,
          position: 'top-right',
        });
      })
      .catch(error => {
        console.error('There was a problem with the reset password operation:', error);
        toast({
          message: 'Error sending password reset email.',
          type: 'is-danger',
          dismissible: true,
          duration: 5000,
          position: 'top-right',
        });
      });
    },
    confirmDeleteUser() {
      if (window.confirm('Are you sure you want to delete this user?')) {
        this.deleteUser();
      }
    },
    deleteUser() {
      axios.delete(`/api/v1/user/delete-user/${this.localUser.id}/`)
      .then(response => {
        console.log('User deleted:', response.data);
        toast({
          message: 'User deleted successfully.',
          type: 'is-success',
          dismissible: true,
          duration: 3000,
          position: 'top-right',
        });
        this.$emit('close');
      })
      .catch(error => {
        console.error('There was a problem with the delete user operation:', error);
        toast({
          message: 'Error deleting user.',
          type: 'is-danger',
          dismissible: true,
          duration: 5000,
          position: 'top-right',
        });
      });
    },
  },
};
</script>

<style scoped>
/* Remove the default arrow for select elements */
select.no-arrow {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background: none;
  padding-right: 2em; /* Adjust to make space for your own icon or extra padding */
}

/* Hide the arrow by covering it with a pseudo-element */
.select.no-arrow:after {
  display: none;
}
</style>
